import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Plus, Trash2, Search, RefreshCw, Bold, Italic, Code, List, Hash } from "lucide-react";

const MODULES = ["sales", "purchase", "inventory", "manufacturing", "banking", "gate_pass", "compliance", "hr", "dsc", "migration", "reports", "distribution", "brochure"];

export default function HelpArticleEditor() {
  const [articles, setArticles] = useState([]);
  const [current, setCurrent] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setArticles((await api.get("/help/articles")).data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!q.trim()) return articles;
    const n = q.toLowerCase();
    return articles.filter((a) => `${a.title} ${a.module} ${(a.tags || []).join(" ")}`.toLowerCase().includes(n));
  }, [articles, q]);

  const startNew = () => setCurrent({ slug: "", module: "sales", category: "", title: "", tags: [], order: 100, body_md: "" });
  const startEdit = (a) => setCurrent({ ...a, tags: a.tags || [] });

  const save = async () => {
    if (!current.slug || !current.title) return toast.error("Slug and title required");
    setBusy(true);
    try {
      await api.post("/help/articles", { ...current, _managed_by_seed: false });
      toast.success("Article saved");
      await load();
      setCurrent(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!confirm("Delete this article?")) return;
    await api.delete(`/help/articles/${current.slug}`);
    toast.success("Deleted");
    await load(); setCurrent(null);
  };
  const reseed = async () => {
    if (!confirm("Re-seed defaults? User-edited articles are preserved.")) return;
    const r = await api.post("/help/seed");
    toast.success(`Seeded ${r.data.seeded} of ${r.data.total}`);
    load();
  };

  // Toolbar helpers — insert markdown at cursor
  const insert = (before, after = "") => {
    const ta = document.getElementById("help-body");
    if (!ta) return;
    const s = ta.selectionStart, e = ta.selectionEnd;
    const body = current.body_md;
    const next = body.slice(0, s) + before + body.slice(s, e) + after + body.slice(e);
    setCurrent({ ...current, body_md: next });
    setTimeout(() => { ta.focus(); ta.setSelectionRange(s + before.length, e + before.length); }, 0);
  };

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="help-editor">
      {/* Left: list */}
      <Card className="p-4 rounded-sm lg:col-span-1 max-h-[700px] overflow-y-auto">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles" className="rounded-sm border-0 focus-visible:ring-0 h-8" />
        </div>
        <div className="flex gap-1 mb-3">
          <Button size="sm" onClick={startNew} className="rounded-sm gap-1 bg-accent hover:bg-accent/90 text-white flex-1" data-testid="new-article"><Plus className="w-3 h-3" /> New</Button>
          <Button size="sm" onClick={reseed} variant="outline" className="rounded-sm gap-1" data-testid="reseed"><RefreshCw className="w-3 h-3" /></Button>
        </div>
        <div className="space-y-1">
          {filtered.map((a) => (
            <button key={a.slug} onClick={() => startEdit(a)}
              className={`w-full text-left p-2 rounded-sm text-xs border transition-colors ${current?.slug === a.slug ? "bg-accent/10 border-accent/40" : "border-transparent hover:bg-muted/40"}`}
              data-testid={`art-${a.slug}`}>
              <div className="font-semibold truncate">{a.title}</div>
              <div className="text-[10px] text-muted-foreground font-mono uppercase">{a.module} · {a.category}</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Right: editor */}
      <Card className="p-4 rounded-sm lg:col-span-2 space-y-3">
        {!current && <div className="text-center text-muted-foreground italic p-16">Pick an article on the left or click <b>+ New</b>.</div>}
        {current && (
          <>
            <div className="grid md:grid-cols-2 gap-3">
              <div><Label>Slug</Label><Input value={current.slug} onChange={(e) => setCurrent({ ...current, slug: e.target.value })} className="rounded-sm mt-1 font-mono text-xs" placeholder="my-article-slug" data-testid="art-slug" /></div>
              <div><Label>Module</Label>
                <Select value={current.module} onValueChange={(v) => setCurrent({ ...current, module: v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{MODULES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Title</Label><Input value={current.title} onChange={(e) => setCurrent({ ...current, title: e.target.value })} className="rounded-sm mt-1" data-testid="art-title" /></div>
              <div><Label>Category</Label><Input value={current.category} onChange={(e) => setCurrent({ ...current, category: e.target.value })} className="rounded-sm mt-1" data-testid="art-cat" /></div>
              <div className="md:col-span-2"><Label>Tags (comma-separated)</Label>
                <Input value={(current.tags || []).join(", ")} onChange={(e) => setCurrent({ ...current, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })} className="rounded-sm mt-1 font-mono text-xs" />
              </div>
            </div>

            <div>
              <div className="flex items-center gap-1 mb-1">
                <Label className="flex-1">Body (Markdown)</Label>
                <Button size="sm" variant="ghost" onClick={() => insert("**", "**")} className="h-7 px-2" title="Bold"><Bold className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" onClick={() => insert("*", "*")} className="h-7 px-2" title="Italic"><Italic className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" onClick={() => insert("`", "`")} className="h-7 px-2" title="Code"><Code className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" onClick={() => insert("### ")} className="h-7 px-2" title="Heading"><Hash className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" onClick={() => insert("\n- ")} className="h-7 px-2" title="List"><List className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" onClick={() => insert("\n```\n", "\n```\n")} className="h-7 px-2 text-[10px] font-mono" title="Code block">```</Button>
              </div>
              <Textarea id="help-body" value={current.body_md} onChange={(e) => setCurrent({ ...current, body_md: e.target.value })} rows={16} className="rounded-sm font-mono text-xs" placeholder="Write in Markdown — headings, lists, bold, tables all supported." data-testid="art-body" />
              <div className="text-[10px] text-muted-foreground mt-1 font-mono">{current.body_md.length} chars · Preview at /app/help/{current.slug}</div>
            </div>

            <div className="flex gap-2 pt-2 border-t">
              <Button onClick={save} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="art-save"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}</Button>
              {current._managed_by_seed !== undefined && current.slug && (
                <Button onClick={del} variant="outline" className="rounded-sm gap-1.5 text-red-700 border-red-200" data-testid="art-delete"><Trash2 className="w-4 h-4" /> Delete</Button>
              )}
              <Button onClick={() => window.open(`/app/help/${current.slug}`, "_blank")} variant="outline" className="rounded-sm">Preview →</Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
